import { useEffect, useState } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import axios from 'axios';
import Card from '../../../Card/Card';
import InputGroup from '../../../FormElements/InputGroup/InputGroup';
import SelectDropDown from '../../../FormElements/SelectDropDown/SelectDropDown';
import TabActionButtons from '../../../FormElements/TabActionButtons/TabActionButtons';
import ToggleSwitch from '../../../FormElements/ToggleSwitch/ToggleSwitch';
import { currencyData } from '../../../utils/helpers';
import './TabDropdown.css';

const CurrencySettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  const [settings, setSettings] = useState({
    hvnly_EnabledCurrencyFormat: false,
    hvnly_thousandSeparator: ',',
    hvnly_decimalSeparator: '.',
    hvnly_numberOfDecimals: '0',
    hvnly_thousandText: 'K',
    hvnly_millionText: 'M',
    hvnly_billionText: 'B',
    hvnly_currencyType: 'USD',
    hvnly_currencyPositionType: 'LEFT',
    hvnly_priceOnCallText: 'priceOnCallNone',
    hvnly_priceFormat: 'comma',
  });

  const [allOptions, setAllOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [newCustomLabel, setNewCustomLabel] = useState('');
  const [editingOption, setEditingOption] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const API_BASE = window.HavenlyticsData?.hvnlyNabApi?.url || '/wp-json/hvnlynab/v1';
  const API_NONCE = window.HavenlyticsData?.hvnlyNabApi?.nonce || '';

  const loadAllOptions = async () => {
    setIsLoadingOptions(true);
    try {
      const response = await axios.get(`${API_BASE}/price-on-call-texts`, {
        headers: { 'X-WP-Nonce': API_NONCE }
      });
      if (response.data?.success && response.data?.data) {
        setAllOptions(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load options:', error);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  useEffect(() => {
    loadAllOptions();
  }, []);

  useEffect(() => {
    if (settingsData && typeof settingsData === 'object' && Object.keys(settingsData).length > 0) {
      setSettings(prev => ({ ...prev, ...settingsData }));
    }
  }, [settingsData]);

  const handleInputChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const getPriceOnCallOptions = () => {
    return allOptions.map(opt => ({
      value: opt.value,
      label: opt.label,
      id: opt.id,
      isDefault: opt.is_default === 1 || opt.is_default === '1'
    }));
  };

  // UPDATED: Removed auto-selection of the newly created option
  const handleCreateCustomOption = async () => {
    if (!newCustomLabel.trim()) {
      showToast?.('warning', __('Warning', 'havenlytics'), __('Please enter a label', 'havenlytics'));
      return;
    }

    setIsLoadingOptions(true);
    try {
      const response = await axios.post(
        `${API_BASE}/price-on-call-texts`,
        { label: newCustomLabel.trim() },
        { headers: { 'X-WP-Nonce': API_NONCE, 'Content-Type': 'application/json' } }
      );

      if (response.data?.success) {
        await loadAllOptions();
        
        // REMOVED: Auto-select the newly created option
        // Just show success message and reset form
        setNewCustomLabel('');
        setShowCustomForm(false);
        setEditingOption(null);
        showToast?.('success', __('Success', 'havenlytics'), response.data.message);
      } else {
        showToast?.('error', __('Error', 'havenlytics'), response.data?.message || __('Failed to create option', 'havenlytics'));
      }
    } catch (error) {
      const message = error.response?.data?.message || __('Failed to create custom option', 'havenlytics');
      showToast?.('error', __('Error', 'havenlytics'), message);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const handleUpdateCustomOption = async () => {
    if (!editingOption || !newCustomLabel.trim()) {
      return;
    }

    const oldValue = editingOption.value;
    const wasSelected = settings.hvnly_priceOnCallText === oldValue;

    setIsLoadingOptions(true);
    try {
      const response = await axios({
        method: 'put',
        url: `${API_BASE}/price-on-call-texts/${editingOption.id}`,
        data: { label: newCustomLabel.trim() },
        headers: { 
          'X-WP-Nonce': API_NONCE,
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.success) {
        await loadAllOptions();
        
        // If this option was selected, update the selection to the new value
        if (wasSelected && response.data.data?.value) {
          handleInputChange('hvnly_priceOnCallText', response.data.data.value);
        }
        
        setEditingOption(null);
        setNewCustomLabel('');
        setShowCustomForm(false);
        showToast?.('success', __('Success', 'havenlytics'), response.data.message);
      } else {
        showToast?.('error', __('Error', 'havenlytics'), response.data?.message || __('Failed to update option', 'havenlytics'));
      }
    } catch (error) {
      console.error('Update error:', error);
      const message = error.response?.data?.message || __('Failed to update custom option', 'havenlytics');
      showToast?.('error', __('Error', 'havenlytics'), message);
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const handleDeleteCustomOption = async (optionId, optionLabel, optionValue) => {
    const wasSelected = settings.hvnly_priceOnCallText === optionValue;
    
    if (window.HvnlyNabAlert) {
      const result = await window.HvnlyNabAlert.confirm(
        sprintf(__('Are you sure you want to delete "%s"? This cannot be undone.', 'havenlytics'), optionLabel),
        __('Delete Custom Option?', 'havenlytics')
      );
      if (result !== 'confirm') {
        return;
      }
    } else {
      if (!confirm(sprintf(__('Are you sure you want to delete "%s"?', 'havenlytics'), optionLabel))) {
        return;
      }
    }

    setIsDeleting(true);
    try {
      const response = await axios({
        method: 'delete',
        url: `${API_BASE}/price-on-call-texts/${optionId}`,
        headers: { 'X-WP-Nonce': API_NONCE }
      });
      
      if (response.data?.success === true) {
        await loadAllOptions();
        
        // If the deleted option was selected, reset to "None"
        if (wasSelected) {
          handleInputChange('hvnly_priceOnCallText', 'priceOnCallNone');
        }
        
        showToast?.('success', __('Success', 'havenlytics'), response.data.message);
      } else {
        showToast?.('error', __('Error', 'havenlytics'), response.data?.message || __('Failed to delete option', 'havenlytics'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      let errorMessage = __('Failed to delete custom option', 'havenlytics');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 403) {
        errorMessage = __('Default options cannot be deleted', 'havenlytics');
      }
      showToast?.('error', __('Error', 'havenlytics'), errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper function to sanitize title (same as PHP sanitize_title)
  const sanitizeTitle = (text) => {
    return text.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const startEditing = (option) => {
    setEditingOption(option);
    setNewCustomLabel(option.label);
    setShowCustomForm(true);
  };

  const cancelCustomForm = () => {
    setEditingOption(null);
    setNewCustomLabel('');
    setShowCustomForm(false);
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(settings);
      return true;
    }
    return false;
  };

  const handleReset = async () => {
    const defaultSettings = {
      hvnly_EnabledCurrencyFormat: false,
      hvnly_thousandSeparator: ',',
      hvnly_decimalSeparator: '.',
      hvnly_numberOfDecimals: '0',
      hvnly_thousandText: 'K',
      hvnly_millionText: 'M',
      hvnly_billionText: 'B',
      hvnly_currencyType: 'USD',
      hvnly_currencyPositionType: 'LEFT',
      hvnly_priceOnCallText: 'priceOnCallNone',
      hvnly_priceFormat: 'comma',
    };
    setSettings(defaultSettings);
    if (onSettingsChange) {
      onSettingsChange(defaultSettings);
    }
    if (onReset) {
      await onReset(defaultSettings);
      return true;
    }
    return false;
  };

  const currencyOptions = currencyData;
  const currencyPositionOptions = [
    { value: 'LEFT', label: __('Left', 'havenlytics') },
    { value: 'RIGHT', label: __('Right', 'havenlytics') },
  ];
  const formatOptions = [
    { value: 'comma', label: __('1,000.00 (Comma separator)', 'havenlytics') },
    { value: 'dot', label: __('1.000,00 (Dot separator)', 'havenlytics') },
    { value: 'space', label: __('1 000.00 (Space separator)', 'havenlytics') },
  ];

  const priceOnCallOptions = getPriceOnCallOptions();
  
  // Filter only user-created options (not default ones)
  const userCreatedOptions = allOptions.filter(opt => {
    const isDefault = parseInt(opt.is_default) === 1;
    return !isDefault;
  });

  return (
    <div className='hvnly-settings-api__content hvnly-settings-api__content--active' id="currency">
      <h2>{__('Currency', 'havenlytics')}</h2>

      <Card title={__("Currency Options", "havenlytics")} icon="fas fa-dollar-sign" collapsible>
        <SelectDropDown
          label={__("Select Currency", "havenlytics")}
          id="hvnly_currencyType"
          value={settings.hvnly_currencyType}
          options={currencyOptions}
          onChange={(value) => handleInputChange('hvnly_currencyType', value)}
        />
        <SelectDropDown
          label={__("Currency Position", "havenlytics")}
          id="hvnly_currencyPositionType"
          value={settings.hvnly_currencyPositionType}
          options={currencyPositionOptions}
          onChange={(value) => handleInputChange('hvnly_currencyPositionType', value)}
        />
        
        <SelectDropDown
          label={__("Price on call Text", "havenlytics")}
          id="hvnly_priceOnCallText"
          value={settings.hvnly_priceOnCallText}
          options={priceOnCallOptions}
          onChange={(value) => handleInputChange('hvnly_priceOnCallText', value)}
        />
        
        <div className="hvnly-custom-options-management" style={{ marginTop: '15px',marginBottom: '15px', padding: '15px', background: 'var(--hvnly-color-bg-light)', borderRadius: 'var(--hvnly-border-radius-md)', border: '1px solid var(--hvnly-border-color)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--hvnly-text-primary)' }}>
              {__('Manage Custom Options', 'havenlytics')}
            </h4>
            <button
              type="button"
              className="hvnly-settings-api__modern-btn hvnly-settings-api__modern-btn--outline"
              onClick={() => {
                setShowCustomForm(!showCustomForm);
                if (!showCustomForm) {
                  setEditingOption(null);
                  setNewCustomLabel('');
                }
              }}
              style={{ fontSize: '12px', padding: '5px 12px' }}
            >
              <i className="fas fa-plus"></i>
              {showCustomForm ? __('Cancel', 'havenlytics') : __('Add Custom', 'havenlytics')}
            </button>
          </div>
          
          {showCustomForm && (
            <div className="hvnly-custom-option-form" style={{ marginTop: '10px', padding: '12px', background: 'var(--hvnly-color-bg-white)', borderRadius: 'var(--hvnly-border-radius-sm)' }}>
              <InputGroup
                label={editingOption ? __('Edit Label', 'havenlytics') : __('New Custom Label', 'havenlytics')}
                id="custom_price_on_call_label"
                type="text"
                value={newCustomLabel}
                onChange={(value) => setNewCustomLabel(value)}
                placeholder={__('e.g., Contact Agent, Negotiable, Upon Request', 'havenlytics')}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="hvnly-settings-api__modern-btn hvnly-settings-api__modern-btn--primary"
                  onClick={editingOption ? handleUpdateCustomOption : handleCreateCustomOption}
                  disabled={isLoadingOptions || !newCustomLabel.trim()}
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                >
                  {isLoadingOptions ? (
                    <><i className="fas fa-spinner fa-spin"></i> {__('Saving...', 'havenlytics')}</>
                  ) : (
                    <>{editingOption ? __('Update', 'havenlytics') : __('Create', 'havenlytics')}</>
                  )}
                </button>
                <button
                  type="button"
                  className="hvnly-settings-api__modern-btn hvnly-settings-api__modern-btn--secondary"
                  onClick={cancelCustomForm}
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                >
                  {__('Cancel', 'havenlytics')}
                </button>
              </div>
            </div>
          )}
          
          {userCreatedOptions.length > 0 && (
            <div style={{ marginTop: '15px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {userCreatedOptions.map(option => {
                  const isSelected = settings.hvnly_priceOnCallText === option.value;
                  
                  return (
                    <div 
                      key={option.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        background: isSelected ? 'rgba(108, 96, 254, 0.1)' : 'var(--hvnly-color-bg-white)',
                        border: `1px solid ${isSelected ? 'var(--hvnly-brand-primary)' : 'var(--hvnly-border-color)'}`,
                        borderRadius: 'var(--hvnly-border-radius-sm)'
                      }}
                    >
                      <div>
                        <span style={{ 
                          fontWeight: isSelected ? '600' : '500', 
                          color: isSelected ? 'var(--hvnly-brand-primary)' : 'var(--hvnly-text-primary)' 
                        }}>
                          {option.label}
                          {isSelected && (
                            <span style={{ 
                              marginLeft: '8px', 
                              fontSize: '10px', 
                              color: 'var(--hvnly-brand-primary)',
                              fontWeight: 'normal'
                            }}>
                              ({__('Selected', 'havenlytics')})
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--hvnly-text-secondary)', marginLeft: '8px' }}>
                          ({option.value})
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className="button-link"
                          onClick={() => startEditing(option)}
                          style={{ 
                            color: 'var(--hvnly-brand-primary)', 
                            textDecoration: 'none', 
                            cursor: 'pointer', 
                            background: 'none', 
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: 'var(--hvnly-border-radius-sm)',
                            transition: 'all var(--hvnly-transition-time)'
                          }}
                          title={__('Edit', 'havenlytics')}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          type="button"
                          className="button-link"
                          onClick={() => handleDeleteCustomOption(option.id, option.label, option.value)}
                          disabled={isDeleting}
                          style={{ 
                            color: 'var(--hvnly-brand-error)', 
                            textDecoration: 'none', 
                            cursor: 'pointer', 
                            background: 'none', 
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: 'var(--hvnly-border-radius-sm)',
                            transition: 'all var(--hvnly-transition-time)'
                          }}
                          title={__('Delete', 'havenlytics')}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {userCreatedOptions.length === 0 && !showCustomForm && (
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: 'var(--hvnly-text-secondary)', fontStyle: 'italic' }}>
              {__('No custom options yet. Click "Add Custom" to create one.', 'havenlytics')}
            </p>
          )}
        </div>
        
        <SelectDropDown
          label={__("Price Format", "havenlytics")}
          id="hvnly_priceFormat"
          value={settings.hvnly_priceFormat}
          options={formatOptions}
          onChange={(value) => handleInputChange('hvnly_priceFormat', value)}
        />
        <InputGroup
          label={__("Thousand Separator", "havenlytics")}
          id="hvnly_thousandSeparator"
          type="text"
          value={settings.hvnly_thousandSeparator}
          onChange={(value) => handleInputChange("hvnly_thousandSeparator", value)}
        />
        <InputGroup
          label={__("Decimal Separator", "havenlytics")}
          id="hvnly_decimalSeparator"
          type="text"
          value={settings.hvnly_decimalSeparator}
          onChange={(value) => handleInputChange("hvnly_decimalSeparator", value)}
        />
        <InputGroup
          label={__("Number of Decimals", "havenlytics")}
          id="hvnly_numberOfDecimals"
          type="text"
          value={settings.hvnly_numberOfDecimals}
          onChange={(value) => handleInputChange("hvnly_numberOfDecimals", value)}
        />
      </Card>

      <Card title={__("Currency Thousand/Million/Billion Format", "havenlytics")} icon="fas fa-chart-line" collapsible>
        <InputGroup
          label={__("Thousand Text - K or Thousand", "havenlytics")}
          id="hvnly_thousandText"
          type="text"
          value={settings.hvnly_thousandText}
          onChange={(value) => handleInputChange("hvnly_thousandText", value)}
        />
        <InputGroup
          label={__("Million Text - M or Million", "havenlytics")}
          id="hvnly_millionText"
          type="text"
          value={settings.hvnly_millionText}
          onChange={(value) => handleInputChange("hvnly_millionText", value)}
        />
        <InputGroup
          label={__("Billion Text - B or Billion", "havenlytics")}
          id="hvnly_billionText"
          type="text"
          value={settings.hvnly_billionText}
          onChange={(value) => handleInputChange("hvnly_billionText", value)}
        />
        <ToggleSwitch
          id="hvnly_EnabledCurrencyFormat"
          checked={settings.hvnly_EnabledCurrencyFormat}
          onChange={(checked) => handleInputChange('hvnly_EnabledCurrencyFormat', checked)}
          label={__("Enable Currency Text Format", "havenlytics")}
        />
      </Card>

      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save Currency Settings", "havenlytics")}
        resetLabel={__("Reset Changes", "havenlytics")}
      />
    </div>
  );
};

export default CurrencySettings;